/**
 * DataChat — Collapsible admin data query bar.
 * Appears at the top of every admin page (embedded in AdminLayout).
 * Superadmin only (info@turnliteracy.com).
 */

import React, { useState, useRef, useEffect } from 'react';
import { MessageSquareText, Send, ChevronDown, ChevronUp, Loader2, X } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/lib/auth';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sql?: string | null;
}

export const DataChat: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Only show for superadmin
  if (user?.email !== 'info@turnliteracy.com') return null;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      inputRef.current?.focus();
    }
  }, [isOpen, messages.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Not authenticated.' }]);
        return;
      }

      // Send conversation history for context
      const history = messages.map(m => ({ role: m.role, content: m.content }));

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data-chat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ question, history }),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.error || 'Request failed.' }]);
        return;
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.answer,
        sql: data.sql,
      }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error.' }]);
    } finally {
      setLoading(false);
    }
  };

  // Collapsed: just a small toggle bar
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center gap-2 px-4 py-2 bg-black/[0.02] border-b border-[#E5E5E5] hover:bg-white/[0.06] transition-colors"
      >
        <MessageSquareText className="w-4 h-4 text-[#5856D6]" />
        <span className="text-xs text-[#888888]">Ask about your data...</span>
        <ChevronDown className="w-3 h-3 text-[#D4D4D4] ml-auto" />
      </button>
    );
  }

  // Expanded chat
  return (
    <div className="border-b border-[#D4D4D4] bg-black/20">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#E5E5E5]">
        <div className="flex items-center gap-2">
          <MessageSquareText className="w-4 h-4 text-[#5856D6]" />
          <span className="text-xs font-medium text-[#3D3D3D]">Data Assistant</span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={() => setMessages([])}
              className="p-1.5 rounded-lg text-[#D4D4D4] hover:text-[#3D3D3D] hover:bg-black/[0.03] transition-colors"
              title="Clear chat"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 rounded-lg text-[#D4D4D4] hover:text-[#3D3D3D] hover:bg-black/[0.03] transition-colors"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      {messages.length > 0 && (
        <div className="max-h-64 overflow-y-auto px-4 py-3 space-y-3">
          {messages.map((msg, i) => (
            <div key={i} className={`text-sm ${msg.role === 'user' ? 'text-[#1A1A1A]' : 'text-[#3D3D3D]'}`}>
              <span className="text-xs font-medium text-[#D4D4D4] mr-2">
                {msg.role === 'user' ? 'You' : 'AI'}
              </span>
              <span className="whitespace-pre-wrap">{msg.content}</span>
              {msg.sql && (
                <details className="mt-1">
                  <summary className="text-xs text-[#5856D6]/60 cursor-pointer hover:text-[#5856D6]">
                    SQL query
                  </summary>
                  <pre className="mt-1 text-xs text-[#D4D4D4] bg-black/[0.02] rounded-lg p-2 overflow-x-auto font-mono">
                    {msg.sql}
                  </pre>
                </details>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[#888888]">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Querying...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 px-4 py-2.5">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="e.g. How many words are in skill 1-e-red?"
          disabled={loading}
          className="flex-1 bg-black/[0.03] border border-[#D4D4D4] rounded-lg px-3 py-2 text-sm text-[#1A1A1A] placeholder-[#D4D4D4] focus:outline-none focus:border-[#5856D6]/50 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="p-2 rounded-lg bg-[#5856D6] text-[#1A1A1A] disabled:opacity-30 hover:bg-[#5856D6]/80 transition-colors"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};
